'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'

import { PhnxAvatar } from '@/components/01-atoms/Avatar/Avatar'
import { PhnxOffice } from '@/components/04-templates/Office/Office'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok-api'

interface OfficeBlokProps {
  blok: {
    title: string
    Phone: string
    address: string
    city: string
    state: string
    postcode: string
    Latitude: string
    Longitude: string
    mailingAddress: any
    officeHours: any
    settings: any
    customAdviserOrder: boolean
    customAdvisers: any[]
  } & SbBlokData
  uuid: string
  name: string
}

const Office: FC<OfficeBlokProps> = ({ blok, uuid, name }) => {
  const [advisors, setAdvisors] = useState<any[]>([])

  useEffect(() => {
    const fetchAndSortAdvisers = async () => {
      const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'
      const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

      const sbParams = {
        ...searchParamsData,
        filter_query: {
          component: { in: 'adviser' },
          offices: { any_in_array: uuid },
          __or: [
            { 'settings.location_available': { in: location } },
            { 'settings.location_available': { not_in: filterLocation } },
          ],
        },
        sort_by: 'content.title:asc',
        per_page: 100,
      }

      const storyblokApi = getStoryblokApi()
      const { data } = await storyblokApi.get(`cdn/stories/`, sbParams)

      if (data?.stories) {
        const allAdvisors = data.stories.map((story: any) => ({
          uuid: story.uuid,
          title: story.name,
          role: story.content.title,
          link: `/${story.full_slug}`,
          avatar: story.content.image?.filename ? (
            <PhnxAvatar
              size="large"
              style="square"
              asset={{ src: story.content.image.filename, alt: story.content.image.alt }}
            />
          ) : null,
          biography: Array.isArray(story.content.bio)
            ? story.content.bio.map((child: SbBlokData) => (
                <StoryblokServerComponent blok={child} key={child._uid} />
              ))
            : null,
          specialties: story.content.specialities.map((speciality: any) => ({
            title: speciality,
            href: `/advisers/search?specialty=${speciality}`,
          })),
        }))

        const sortedAdvisors = blok.customAdviserOrder
          ? [
              ...blok.customAdvisers
                .map((customUuid: string) =>
                  allAdvisors.find((advisor: any) => advisor.uuid === customUuid)
                )
                .filter(Boolean),
              // Ensure allAdvisors is always sorted alphabetically
              ...allAdvisors
                .filter((advisor: any) => !blok.customAdvisers.includes(advisor.uuid))
                .sort((a: any, b: any) => a.title.localeCompare(b.title)),
            ]
          : allAdvisors.sort((a: any, b: any) => a.title.localeCompare(b.title))

        setAdvisors(sortedAdvisors)
      }
    }

    fetchAndSortAdvisers()
  }, [blok.customAdvisers, blok.customAdviserOrder, uuid])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxOffice
        name={blok.title || name}
        phone={blok.Phone}
        address={blok.address}
        city={blok.city}
        state={blok.state}
        postcode={blok.postcode}
        country={blok?.settings[0]?.location_available.toUpperCase().replace('EN-', '')}
        latitude={Number(blok.Latitude)}
        longitude={Number(blok.Longitude)}
        mailingAddress={
          Array.isArray(blok.mailingAddress)
            ? blok.mailingAddress.map((child: SbBlokData) => (
                <StoryblokServerComponent blok={child} key={child._uid} />
              ))
            : null
        }
        officeHours={
          Array.isArray(blok.officeHours)
            ? blok.officeHours.map((child: SbBlokData) => (
                <StoryblokServerComponent blok={child} key={child._uid} />
              ))
            : null
        }
        relatedAdvisors={advisors}
      />
    </div>
  )
}

export default Office
